import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  sendEmailVerification
} from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { auth, db } from '../services/firebase';
import { createUserProfile } from '../services/userService';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // IMPORTANT: userProfile includes DEPRECATED brand fields (brandName, brandLogoUrl, brandColors)
  // DO NOT use these fields in new code. Brand data is now stored in workspaces.
  // Use WorkspaceContext.currentWorkspace for brand identity instead.
  // These fields are maintained only for backwards compatibility during migration.

  async function signup(email, password, displayName) {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(result.user, {
      displayName: displayName
    });

    // Send a verification email so sensitive actions can be gated on a
    // confirmed address. Failure here must not block account creation.
    try {
      await sendEmailVerification(result.user);
    } catch (error) {
      console.error('[Auth] Failed to send verification email:', error);
    }

    // Create user profile in Firestore
    await createUserProfile(result.user.uid, {
      email: result.user.email,
      displayName: displayName
    });

    return result;
  }

  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  function resendVerificationEmail() {
    if (!auth.currentUser) {
      return Promise.reject(new Error('No authenticated user'));
    }
    return sendEmailVerification(auth.currentUser);
  }

  function logout() {
    return signOut(auth);
  }

  useEffect(() => {
    let unsubscribeProfile = null;

    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);

      if (user) {
        // Set up real-time listener for user profile
        const userDocRef = doc(db, 'users', user.uid);
        unsubscribeProfile = onSnapshot(userDocRef, (docSnapshot) => {
          if (docSnapshot.exists()) {
            setUserProfile({ id: docSnapshot.id, ...docSnapshot.data() });
          } else {
            setUserProfile(null);
          }
          setLoading(false);
        });
      } else {
        setUserProfile(null);
        setLoading(false);
      }
    });

    return () => {
      unsubscribe();
      if (unsubscribeProfile) {
        unsubscribeProfile();
      }
    };
  }, []);

  const value = {
    currentUser,
    userProfile,
    loading,
    emailVerified: currentUser?.emailVerified ?? false,
    signup,
    login,
    logout,
    resendVerificationEmail
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
} 